import React from 'react';
import { useDispatch } from 'react-redux';
import { Chip } from '@material-ui/core';
import { format } from 'date-fns';
import Countdown from 'react-countdown';
import pluralize from 'pluralize';

import { getRequest } from 'utils/api';
import { openModal } from 'features/utilsSlice';
import useCurrentUser from 'hooks/useCurrentUser';
import { formatPrice, getGigOwner } from 'utils/helpers';
import useIsBuyer from 'hooks/useIsBuyer';
import ChevronRight from 'public/icons/chevron-right-grey.svg';
import styles from 'styles/components/ToDos.module.scss';

const SimpleToDoCard = ({ todo }) => {
  const dispatch = useDispatch();
  const isBuyer = useIsBuyer();
  const currentUser = useCurrentUser();
  const gigOwner = getGigOwner(currentUser, todo.user_id);

  const overdue =
    new Date() > new Date(todo.latest_start_time) && !todo.posted_at && todo.status !== 'approved';

  // else if (status === 'pending' && !todo.posted_at)

  const getIndicatorColor = () => {
    const { status } = todo;

    if (status === 'approved') {
      return '#12dfc7';
    } else if (status === 'rejected') {
      return '#ff4b4b';
    } else if (status === 'pending') {
      return '#12dfc7';
    } else if (todo.posted_at && new Date() >= todo.expires_at) {
      return '#5b00db';
    } else if (new Date() >= todo.expires_at) {
      return '#ff52ca';
    }
  };

  const getPrice = () => {
    if (!gigOwner) return formatPrice(todo.amount);

    const baseAmount = gigOwner ? todo.total_amount : todo.amount;
    const amount = todo.guaranteed_gains
      ? (todo.guaranteed_gains_current / todo.guaranteed_gains_max) * baseAmount
      : baseAmount;

    return formatPrice(amount);
  };

  const getToDoInfluencerTitle = () => {
    const { status } = todo;

    if (status === 'approved') {
      return 'ToDo Approved';
    } else if (status === 'rejected') {
      return `${todo.advertiser_name} has rejected your post`;
    } else if (status === 'awaiting_approval') {
      return 'Awaiting approval';
    } else if (
      status === 'pending' &&
      !todo.posted_at &&
      new Date() >= new Date(todo.earliest_start_time)
    ) {
      return 'Time to post';
    } else if (todo.posted_at && new Date() <= new Date(todo.expires_at)) {
      return (
        <Countdown
          date={new Date(todo.expires_at)}
          intervalDelay={60000}
          renderer={({ days, hours, minutes }) => {
            if (days >= 2 && hours >= 0) {
              return `Take down in ${days} days ${hours > 0 &&
                `${hours} ${pluralize('hours', hours)}`} `;
            } else if (hours > 0 && days < 2) {
              const dayToHours = days * 24;
              return `Take down in ${hours + dayToHours} ${pluralize('hours', hours)}`;
            } else if (minutes > 0 && hours === 0) {
              return `Take down in ${minutes} ${pluralize('minutes', minutes)}`;
            } else {
              return '0 minutes';
            }
          }}
        />
      );
    } else if (todo.posted_at && new Date() >= new Date(todo.expires_at)) {
      return todo.guaranteed_gains
        ? "You can now track your analytics on the buyer's OnlyFans campaign."
        : 'Time to take down the post and upload delivery screenshot';
    }
  };

  const getToDoAdvertiserTitle = () => {
    const { status } = todo;

    if (status === 'approved') {
      return 'ToDo Approved';
    } else if (status === 'awaiting_approval') {
      return 'Time to approve post';
    } else return 'Awaiting delivery'; // Advertiser should have only todos when he shoul approve/reject
  };

  const getDescription = () => {
    if (todo.status === 'approved') {
      return `Approved: ${format(new Date(todo.approved_at), 'MM/dd/yy')}`;
    } else if (todo.status === 'rejected' && todo.rejection_reason) {
      return todo.rejection_reason;
    } else {
      return `Order #${todo.gig_id} for  ${isBuyer ? todo.influencer_name : todo.advertiser_name}`;
    }
  };

  const handleOpenToDo = () => {
    Promise.all([
      getRequest({
        endpoint: `gigs/${todo.gig_id}`,
      }),
      getRequest({
        endpoint: `milestones/${todo.id}`,
      }),
    ])
      .then(([gig, milestone]) => {
        dispatch(openModal({ name: 'New Milestone', data: { gig, milestone } }));
      })
      .catch(() => {});
  };

  return (
    <div
      className={styles.card}
      onClick={handleOpenToDo}
      style={todo.approved_at ? { opacity: 0.5 } : {}}
    >
      <div className={styles.column}>
        <div className={styles.indicator} style={{ backgroundColor: getIndicatorColor() }} />

        <div style={{ paddingLeft: 10, marginRight: 'auto' }}>
          <h3>{isBuyer ? getToDoAdvertiserTitle() : getToDoInfluencerTitle()}</h3>
          <p>{getDescription()}</p>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {overdue && (
          <div className={styles.column}>
            <Chip
              label={todo.guaranteed_gains ? 'In Progress' : 'Overdue'}
              color="secondary"
              className={styles.chip}
            />
            <ChevronRight />
          </div>
        )}

        <div
          className={styles.price}
          style={overdue ? { marginBottom: 0, marginRight: 25, marginTop: 5 } : {}}
        >
          <span>{getPrice()}</span>
        </div>
      </div>
    </div>
  );
};

export default SimpleToDoCard;
